/**
 * TEMPORARY: Direct Admin Access Page
 * Use this to bypass admin checks and access admin panel
 * Remove after fixing authentication
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';

export default function AdminForceAccess() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Checking...');
  const [details, setDetails] = useState<any>({});

  useEffect(() => {
    const checkAndFix = async () => {
      if (!user) {
        setStatus('❌ Not logged in');
        return;
      }

      try {
        setStatus('🔍 Checking admin document...');
        setDetails({ uid: user.uid, email: user.email });

        const adminRef = doc(db, 'adminUsers', user.uid);
        const adminSnap = await getDoc(adminRef);

        if (adminSnap.exists()) {
          const data = adminSnap.data();
          setStatus('✅ Admin document exists!');
          setDetails({
            ...details,
            adminData: data,
            isActive: data.isActive,
            role: data.role
          });

          if (!data.isActive) {
            setStatus('⚠️ Admin account is inactive. Activating...');
            await setDoc(adminRef, { ...data, isActive: true }, { merge: true });
            setStatus('✅ Admin account activated!');
          }
        } else {
          setStatus('❌ No admin document found. Creating...');
          
          const adminData = {
            id: user.uid,
            uid: user.uid,
            email: user.email || '',
            fullName: user.displayName || 'Admin User',
            role: 'super_admin',
            permissions: {
              manageUsers: true,
              manageOrders: true,
              manageServices: true,
              manageProviders: true,
              viewReports: true,
              manageSettings: true,
              manageFeedback: true,
              managePayments: true
            },
            isActive: true,
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString()
          };

          await setDoc(adminRef, adminData);
          setStatus('✅ Admin document created!');
          setDetails({ ...details, adminData });
        }
      } catch (error: any) {
        setStatus(`❌ Error: ${error.message}`);
        console.error('Error:', error);
      }
    };

    checkAndFix();
  }, [user]);

  const handleGoToAdmin = () => {
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          🔧 Admin Access Fix
        </h1>

        <div className="space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
            <p className="font-semibold text-blue-900">Status:</p>
            <p className="text-blue-800 mt-1">{status}</p>
          </div>

          {user && (
            <div className="bg-gray-50 p-4 rounded">
              <p className="font-semibold mb-2">Current User:</p>
              <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-auto">
                {JSON.stringify(details, null, 2)}
              </pre>
            </div>
          )}

          {status.includes('✅') && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4">
              <p className="text-green-800 font-semibold mb-2">
                ✅ Admin access configured!
              </p>
              <button
                onClick={handleGoToAdmin}
                className="mt-3 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Go to Admin Panel →
              </button>
            </div>
          )}

          {!user && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <p className="text-red-800">
                Please log in first, then come back to this page.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="mt-3 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Go to Login →
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t text-sm text-gray-500">
          <p className="font-semibold mb-2">What this page does:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Checks if admin document exists for your user</li>
            <li>Creates admin document if missing</li>
            <li>Activates admin account if inactive</li>
            <li>Shows detailed status and user info</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
